/**
 * AddCommunityMessageUseCase
 *
 * Persists an ALREADY-BUILT community-wall message via
 * `CommunityService.addMessage` and reports the write outcome. The optimistic
 * entity construction now lives in `CommunityChatMessageRemapper.fromInput`
 * (the canonical mapper for this type) and the optimistic insert happens in the
 * hook — so this use case no longer builds the entity, it receives it. This
 * keeps the build a single source of truth and lets the presentation layer
 * mirror Flutter's optimistic ordering: the hook builds the entity via the
 * remapper, inserts it at index 0 (and clears the reply / scrolls to top)
 * BEFORE awaiting the write — the whole point of the "add message in list
 * before saving to DB" comment (community_screen.dart:532-548).
 *
 * Flutter source: lib/presentation/screens/community/community_screen.dart:459-550
 *   (`addMessage`, text branch).
 *   - `community_screen.dart:548` — `CommunityRepository.addMessage(message, null)`.
 *
 * Documented divergence from the chat send path (business-parity reviewer's
 * note): unlike the chat flow, the community `addMessage`
 * (community_screen.dart:459-550) never mutates the in-list message's `success`
 * on a failed write (the model default `success = true`,
 * community_chat_mssage.dart). We mirror that here: this use case only reports
 * the write boolean and never touches the passed entity's `success`. The
 * behavioural divergence of removing a failed TEXT message from the list lives
 * in the hook, not here.
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow):
 *   - Calls `CommunityService.addMessage`; does NOT touch `firestoreService`
 *     directly and does NOT dispatch to Redux.
 *   - Community messages are NOT end-to-end encrypted (no encrypt step) — the
 *     one structural difference from the chat send path.
 */

import { CommunityService } from '@/domain/services/community/communityService';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';

export interface AddCommunityMessageResult {
  /** `true` on a successful Firestore write, `false` otherwise. */
  success: boolean;
}

export class AddCommunityMessageUseCase {
  private communityService: CommunityService;

  constructor(communityService: CommunityService = CommunityService.getInstance()) {
    this.communityService = communityService;
  }

  /**
   * Persist an already-built message via `CommunityService.addMessage` — the
   * Firestore write only. The hook builds the entity via
   * `CommunityChatMessageRemapper.fromInput`, does the optimistic insert, then
   * awaits this (`CommunityRepository.addMessage(message, null)`,
   * community_screen.dart:548).
   *
   * @returns `{ success: true }` on a successful write, `{ success: false }`
   *   otherwise. The passed entity is never mutated — see the documented
   *   divergence in the class JSDoc.
   */
  async execute(message: CommunityChatMessageEntity): Promise<AddCommunityMessageResult> {
    const success = await this.communityService.addMessage(message);
    return { success };
  }
}

export const addCommunityMessageUseCase = new AddCommunityMessageUseCase();
